// A Boo's People page: People | Groups | Settings | History, each `?tab=`,
// plus the paging and search its people list uses.

import type { PersonRow } from "./api.ts";
import { chatGroups, displayName, groupLabel, levelLabel, levelOf, platformLabel } from "./people-levels.ts";

export const PEOPLE_TABS = ["people", "groups", "settings", "history"] as const;
export type PeopleTab = (typeof PEOPLE_TABS)[number];

export type PeopleSearch = { tab?: PeopleTab; group?: string };

export function validatePeopleSearch(search: Record<string, unknown>): PeopleSearch {
  return {
    ...(PEOPLE_TABS.includes(search.tab as PeopleTab) ? { tab: search.tab as PeopleTab } : {}),
    ...(typeof search.group === "string" && search.group ? { group: search.group } : {}),
  };
}

/**
 * The tab to show. `?group=` and the old `#groups` anchor (links from before
 * the tabs) both mean Groups when no tab is named.
 */
export function activePeopleTab(search: PeopleSearch, hash: string): PeopleTab {
  if (search.tab) return search.tab;
  if (search.group || hash === "groups") return "groups";
  return "people";
}

export const PEOPLE_PAGE_SIZE = 25;

export type Page<T> = { items: T[]; page: number; pages: number; from: number; to: number; total: number };

/** One page of `items`, 1-based. A page past the end shows the last one. */
export function pageOf<T>(items: T[], page: number, size = PEOPLE_PAGE_SIZE): Page<T> {
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), pages);
  const start = (current - 1) * size;
  const slice = items.slice(start, start + size);
  return { items: slice, page: current, pages, from: total ? start + 1 : 0, to: start + slice.length, total };
}

/** Every word of the query appears somewhere in the text, in any order, ignoring case and accents. */
export function matchesQuery(text: string, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = fold(text);
  return words.every((w) => hay.includes(w));
}

/** What search reads on a person's row: name, email, access, kind, groups and chat apps. */
export function personSearchText(p: PersonRow): string {
  return [
    displayName(p),
    p.name,
    p.email,
    levelLabel(levelOf(p)),
    p.kind === "chat_only" ? "Chat only" : "Dashboard",
    ...chatGroups(p).map(groupLabel),
    ...p.handles.filter((h) => h.provider !== "dashboard_chat").map((h) => platformLabel(h.provider)),
  ]
    .filter(Boolean)
    .join(" ");
}

function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}
